import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { Buildings, CreditCard, Megaphone, SignOut, List, Key } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { initials } from "../lib/utils";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import ChangePasswordDialog from "./ChangePasswordDialog";

function AdminSidebarBody({ user, logout, navigate, onNavigate = () => {} }) {
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-2.5" onClick={onNavigate} style={{ cursor: "default" }}>
          <div className="w-8 h-8 rounded-md bg-indigo-600 flex items-center justify-center">
            <Buildings weight="fill" size={18} className="text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-base tracking-tight">Platform Admin</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Support By ERP4U</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-5 mb-2 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Admin</div>
        <NavLink
          to="/admin"
          end
          data-testid="admin-nav-companies"
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center px-4 py-2 text-sm font-medium rounded-md mx-2 my-0.5 transition-colors ${
              isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            }`
          }
        >
          <Buildings size={18} weight="regular" className="mr-3" />
          Overview
        </NavLink>
        <NavLink
          to="/admin/plans"
          data-testid="admin-nav-plans"
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center px-4 py-2 text-sm font-medium rounded-md mx-2 my-0.5 transition-colors ${
              isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            }`
          }
        >
          <CreditCard size={18} weight="regular" className="mr-3" />
          Plans
        </NavLink>
        <NavLink
          to="/admin/updates"
          data-testid="admin-nav-updates"
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center px-4 py-2 text-sm font-medium rounded-md mx-2 my-0.5 transition-colors ${
              isActive ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
            }`
          }
        >
          <Megaphone size={18} weight="regular" className="mr-3" />
          Updates
        </NavLink>
      </nav>

      <div className="border-t border-slate-800 p-4 shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-indigo-600/20 text-indigo-300 flex items-center justify-center text-xs font-semibold">
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white truncate" data-testid="admin-sidebar-user-name">{user.name}</div>
            <div className="text-xs text-slate-500 truncate">Platform Admin</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPwOpen(true)}
          data-testid="admin-change-password-button"
          className="w-full inline-flex items-center justify-center gap-2 text-xs text-slate-300 border border-slate-700 hover:bg-slate-800 rounded-md px-3 py-2 mb-2 transition-colors"
        >
          <Key size={14} /> Change password
        </button>
        <button
          type="button"
          onClick={async () => {
            onNavigate();
            await logout();
            navigate("/login");
          }}
          data-testid="admin-logout-button"
          className="w-full inline-flex items-center justify-center gap-2 text-xs text-slate-300 border border-slate-700 hover:bg-slate-800 rounded-md px-3 py-2 transition-colors"
        >
          <SignOut size={14} /> Sign out
        </button>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        <a href="https://webteampl.com" target="_blank" rel="noreferrer"
          className="block text-center text-[10px] text-slate-500 hover:text-slate-300 mt-3">
          A WebTeam product · webteampl.com
        </a>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop rail */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-slate-300 flex-col border-r border-slate-800 sticky top-0 h-screen" data-testid="admin-sidebar">
        <AdminSidebarBody user={user} logout={logout} navigate={navigate} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-slate-900 border-slate-800 text-slate-300 [&>button]:text-slate-400 [&>button]:hover:text-white [&>button]:opacity-100" data-testid="sidebar-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <AdminSidebarBody user={user} logout={logout} navigate={navigate} onNavigate={() => setDrawerOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-4 lg:px-6 sticky top-0 z-10" data-testid="admin-topbar">
          <button type="button" className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-600"
            onClick={() => setDrawerOpen(true)} data-testid="sidebar-toggle" aria-label="Open menu">
            <List size={20} />
          </button>
          <h1 className="text-sm font-semibold text-slate-700 tracking-wide uppercase">Platform Admin</h1>
        </header>

        <main className="flex-1 min-w-0">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
